/**
 * ERM - Enterprise Risk Management
 * Board-Level Risk Intelligence Spine  
 * 2x2 Risk Matrix (Probability x Criticality)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import TabNavigation from '../components/TabNavigation';
import {
  AlertTriangle, TrendingUp, Shield, Users, DollarSign, Zap,
  Plus, Edit2, Eye, Sparkles, Download, Activity, CheckCircle, FileText,
  Calendar, Briefcase, ArrowRight
} from 'lucide-react';
import { useAlert } from '../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const RiskManagement = ({ user, onLogout, hideNavigation = false }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [risks, setRisks] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [riskLibrary, setRiskLibrary] = useState(null);
  const [signals, setSignals] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [businessType, setBusinessType] = useState('Manufacturing');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showRiskDetail, setShowRiskDetail] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [showCreateStudy, setShowCreateStudy] = useState(false);
  const [expandedQuadrant, setExpandedQuadrant] = useState(null);
  const [openStudyDetail, setOpenStudyDetail] = useState(null);
  const [showQuadrantModal, setShowQuadrantModal] = useState(false);
  const [selectedQuadrantData, setSelectedQuadrantData] = useState(null);

  const [newRisk, setNewRisk] = useState({
    risk_title: '',
    category: 'Operational',
    description: '',
    impact_score: 'Medium',
    likelihood_score: 'Medium',
    mitigation_plan: '',
    risk_owner: '',
    target_closure_date: ''
  });

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      // Fetch studies first
      const studiesRes = await axios.get(`${API}/api/erm/studies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudies(studiesRes.data.studies || []);
      
      // Use selected study or latest
      const currentStudy = selectedStudy || studiesRes.data.studies?.[0];
      const studyParam = currentStudy ? `?study_id=${currentStudy.id}` : '';
      
      const industryParam = selectedIndustry === 'All' ? '' : `?industry=${selectedIndustry}`;
      const [risksRes, heatmapRes, libraryRes, signalsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/api/erm/risks${studyParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/erm/heatmap${studyParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/erm/risk-library${industryParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/erm/kri/signals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/erm/dashboard${studyParam}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setRisks(risksRes.data);
      setHeatmap(heatmapRes.data);
      setRiskLibrary(libraryRes.data);
      setSignals(signalsRes.data);
      setDashboard(dashboardRes.data);
      
      if (!selectedStudy && currentStudy) {
        setSelectedStudy(currentStudy);
      }
    } catch (error) {
      console.error('Error fetching ERM data:', error);
    }
  }, [selectedIndustry, selectedStudy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createRisk = async () => {
    if (!selectedStudy) {
      showWarning('Please select or create a risk study first');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/erm/risks`, {
        ...newRisk,
        study_id: selectedStudy.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Risk created successfully!');
      setShowCreateForm(false);
      setShowLibraryPicker(false);
      setNewRisk({ risk_title: '', category: 'Operational', description: '', impact_score: 'Medium', likelihood_score: 'Medium', mitigation_plan: '', risk_owner: '', target_closure_date: '' });
      fetchData();
    } catch (error) {
      showError('Error creating risk: ' + (error.response?.data?.detail || error.message));
    }
  };

  const selectFromLibrary = (libraryRisk) => {
    setNewRisk({
      ...newRisk,
      risk_title: libraryRisk.risk_title,
      category: libraryRisk.category,
      description: libraryRisk.description,
      impact_score: libraryRisk.default_impact,
      likelihood_score: libraryRisk.default_likelihood
    });
    setShowLibraryPicker(false);
  };

  const generateAISuggestions = async () => {
    setGeneratingAI(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/erm/ai-suggest-risks`, { industry: businessType }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiSuggestions(res.data);
      setShowAISuggestions(true);
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingAI(false);
    }
  };

  const getCategoryIcon = (category) => {
    if (category.includes('Financial')) return <DollarSign className="w-4 h-4" />;
    if (category.includes('People') || category.includes('Safety')) return <Users className="w-4 h-4" />;
    if (category.includes('ESG') || category.includes('Sustainability')) return <Shield className="w-4 h-4" />;
    if (category.includes('Cyber')) return <Zap className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  return (
    <div className={hideNavigation ? "" : "min-h-screen bg-gray-50"}>
      {!hideNavigation && <TabNavigation user={user} onLogout={onLogout} />}
      
      <div className={hideNavigation ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {/* Header */}
        {!hideNavigation && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Enterprise Risk Management</h1>
                <p className="text-gray-600 mt-2">Board-Level Risk Intelligence & Governance Spine</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Risk Study:</label>
                    <select
                      value={selectedStudy?.id || ''}
                      onChange={(e) => {
                        const study = studies.find(s => s.id === e.target.value);
                        setSelectedStudy(study);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg font-medium bg-white min-w-[250px]"
                    >
                      {studies.length === 0 && <option value="">No studies yet</option>}
                      {studies.map(study => (
                        <option key={study.id} value={study.id}>
                          {study.study_name} ({study.study_period})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowCreateStudy(true)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                    >
                      + New Study
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Business Type:</label>
                    <select
                      value={businessType}
                      onChange={(e) => setBusinessType(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg font-medium bg-white"
                    >
                      <option value="Manufacturing">🏭 Manufacturing/Steel</option>
                      <option value="Renewable Energy">⚡ Renewable Energy</option>
                      <option value="Technology">💻 Technology/IT</option>
                      <option value="Healthcare">🏥 Healthcare</option>
                      <option value="Financial Services">💰 Financial Services</option>
                      <option value="Retail">🛒 Retail & E-commerce</option>
                      <option value="Others">📦 Others</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Business Type Selector - Show when embedded */}
        {hideNavigation && (
          <div className="flex justify-between mb-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Risk Study:</label>
              <select
                value={selectedStudy?.id || ''}
                onChange={(e) => {
                  const study = studies.find(s => s.id === e.target.value);
                  setSelectedStudy(study);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium bg-white min-w-[250px]"
              >
                {studies.length === 0 && <option value="">No studies yet</option>}
                {studies.map(study => (
                  <option key={study.id} value={study.id}>
                    {study.study_name} ({study.study_period})
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowCreateStudy(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                + New Study
              </button>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Business Type:</label>
              <select
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium bg-white"
              >
                <option value="Manufacturing">🏭 Manufacturing/Steel</option>
                <option value="Renewable Energy">⚡ Renewable Energy</option>
                <option value="Technology">💻 Technology/IT</option>
                <option value="Healthcare">🏥 Healthcare</option>
                <option value="Financial Services">💰 Financial Services</option>
                <option value="Retail">🛒 Retail & E-commerce</option>
                <option value="Others">📦 Others</option>
              </select>
            </div>
          </div>
        )}

        {/* KRI Signals Alert */}
        {signals && signals.total_signals > 0 && (
          <div className="mb-6 bg-orange-50 border-2 border-orange-300 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <Activity className="w-6 h-6 text-orange-600" />
              <h3 className="font-bold text-orange-900">KRI Signals Detected ({signals.total_signals})</h3>
            </div>
            <div className="space-y-2">
              {signals.signals.map((sig, i) => (
                <div key={i} className="flex items-start gap-3 bg-white p-3 rounded border border-orange-200">
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    sig.severity === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {sig.signal_type}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{sig.suggested_risk}</p>
                    <p className="text-sm text-gray-600">{sig.recommendation}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View Selector - Enhanced Design */}
        <div className="mb-6">
          <div className="bg-white rounded-xl border-2 border-gray-200 p-2 shadow-sm">
            <div className="flex gap-1">
              <button
                onClick={() => setActiveView('dashboard')}
                className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                  activeView === 'dashboard' 
                    ? 'bg-red-600 text-white shadow-md' 
                    : 'bg-transparent text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Dashboard
                </span>
              </button>
              <button
                onClick={() => setActiveView('studies')}
                className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                  activeView === 'studies' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'bg-transparent text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Risk Assessment Studies
                </span>
              </button>
              <button
                onClick={() => setActiveView('library')}
                className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                  activeView === 'library' 
                    ? 'bg-purple-600 text-white shadow-md' 
                    : 'bg-transparent text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Risk Library
                </span>
              </button>
            </div>
          </div>
          
          {/* Study Selector for Dashboard */}
          {activeView === 'dashboard' && (
            <div className="flex items-center gap-3 mt-4 justify-between">
              <div className="flex gap-2">
                <button
                  onClick={generateAISuggestions}
                  disabled={generatingAI}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  {generatingAI ? 'Generating...' : 'AI Suggest Risks'}
                </button>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Risk
                </button>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Viewing Study:</label>
                <select
                  value={selectedStudy?.id || ''}
                  onChange={(e) => {
                    const study = studies.find(s => s.id === e.target.value);
                    setSelectedStudy(study);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg font-medium bg-white min-w-[300px]"
                >
                  {studies.length === 0 && <option value="">No studies yet - Create one first</option>}
                  {studies.map(study => (
                    <option key={study.id} value={study.id}>
                      {study.study_name} ({study.study_period})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          
          {/* Action Buttons Row - For non-dashboard views */}
          {activeView !== 'dashboard' && (
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={generateAISuggestions}
                disabled={generatingAI}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                {generatingAI ? 'Generating...' : 'AI Suggest Risks'}
              </button>
              <button
                onClick={() => setShowCreateForm(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Risk
              </button>
            </div>
          )}
        </div>

        {/* AI Suggestions - Collapsible */}
        {aiSuggestions && (
          <div className="mb-6">
            <button
              onClick={() => setShowAISuggestions(!showAISuggestions)}
              className="w-full bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 rounded-xl p-4 transition-all flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h3 className="font-bold text-purple-900">
                  AI Risk Suggestions for {businessType}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-purple-700 font-medium">
                  {showAISuggestions ? 'Hide' : 'Show'} Suggestions
                </span>
                <svg 
                  className={`w-5 h-5 text-purple-600 transition-transform ${showAISuggestions ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            
            {showAISuggestions && (
              <div className="mt-2 bg-purple-50 border-2 border-purple-200 border-t-0 rounded-b-xl p-6">
                <div className="prose prose-sm max-w-none text-gray-700">
                  <ReactMarkdown>{aiSuggestions.suggestions}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dashboard View */}
        {activeView === 'dashboard' && dashboard && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border-2 border-gray-300 p-5">
                <h4 className="text-sm font-medium text-gray-600 mb-1">Total Active Risks</h4>
                <p className="text-3xl font-bold text-gray-900">{dashboard.statistics.total_active_risks}</p>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl border-2 border-red-300 p-5">
                <h4 className="text-sm font-medium text-red-700 mb-1">Inherent Board-Critical</h4>
                <p className="text-3xl font-bold text-red-900">{dashboard.statistics.inherent_board_critical}</p>
                <p className="text-xs text-red-600 mt-1">Before Mitigation</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-300 p-5">
                <h4 className="text-sm font-medium text-green-700 mb-1">Residual Board-Critical</h4>
                <p className="text-3xl font-bold text-green-900">{dashboard.statistics.residual_board_critical}</p>
                <p className="text-xs text-green-600 mt-1">After Mitigation</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-300 p-5">
                <h4 className="text-sm font-medium text-blue-700 mb-1">Mitigation Effectiveness</h4>
                <p className="text-3xl font-bold text-blue-900">{dashboard.statistics.mitigation_effectiveness}%</p>
                <p className="text-xs text-blue-600 mt-1">Risk Reduction</p>
              </div>
            </div>

            {/* Side-by-Side Heatmaps */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Inherent Risk Heatmap */}
              <div className="bg-white rounded-xl border-2 border-gray-300 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Inherent Risk Matrix (Before Mitigation)
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {/* Top Left: Low Probability, High Impact */}
                  <div 
                    onClick={() => {
                      setSelectedQuadrantData({
                        title: 'Severe but Unlikely',
                        subtitle: 'Low Probability × High Impact',
                        risks: dashboard.inherent_heatmap.low_high || [],
                        color: 'orange'
                      });
                      setShowQuadrantModal(true);
                    }}
                    className="bg-orange-400 rounded-lg p-4 min-h-[150px] border-2 border-orange-500 cursor-pointer hover:shadow-lg transition-all"
                  >
                    <div className="text-white mb-2">
                      <h4 className="font-bold text-sm">Severe but Unlikely</h4>
                      <p className="text-xs opacity-90">Low Prob × High Impact</p>
                    </div>
                    <div className="bg-white/20 rounded p-2 text-white">
                      <p className="font-bold text-lg">{dashboard.inherent_heatmap.low_high?.length || 0}</p>
                      <p className="text-xs">risks</p>
                    </div>
                  </div>

                  {/* Top Right: High Probability, High Impact */}
                  <div 
                    onClick={() => {
                      setSelectedQuadrantData({
                        title: '⚠️ BOARD-CRITICAL',
                        subtitle: 'High Probability × High Impact',
                        risks: dashboard.inherent_heatmap.high_high || [],
                        color: 'red'
                      });
                      setShowQuadrantModal(true);
                    }}
                    className="bg-red-500 rounded-lg p-4 min-h-[150px] border-2 border-red-600 cursor-pointer hover:shadow-lg transition-all"
                  >
                    <div className="text-white mb-2">
                      <h4 className="font-bold text-sm">⚠️ BOARD-CRITICAL</h4>
                      <p className="text-xs opacity-90">High Prob × High Impact</p>
                    </div>
                    <div className="bg-white/20 rounded p-2 text-white">
                      <p className="font-bold text-lg">{dashboard.inherent_heatmap.high_high?.length || 0}</p>
                      <p className="text-xs">risks</p>
                    </div>
                  </div>

                  {/* Bottom Left: Low Probability, Low Impact */}
                  <div 
                    onClick={() => {
                      setSelectedQuadrantData({
                        title: 'Acceptable',
                        subtitle: 'Low Probability × Low Impact',
                        risks: dashboard.inherent_heatmap.low_low || [],
                        color: 'green'
                      });
                      setShowQuadrantModal(true);
                    }}
                    className="bg-green-400 rounded-lg p-4 min-h-[150px] border-2 border-green-500 cursor-pointer hover:shadow-lg transition-all"
                  >
                    <div className="text-white mb-2">
                      <h4 className="font-bold text-sm">Acceptable</h4>
                      <p className="text-xs opacity-90">Low Prob × Low Impact</p>
                    </div>
                    <div className="bg-white/20 rounded p-2 text-white">
                      <p className="font-bold text-lg">{dashboard.inherent_heatmap.low_low?.length || 0}</p>
                      <p className="text-xs">risks</p>
                    </div>
                  </div>

                  {/* Bottom Right: High Probability, Low Impact */}
                  <div 
                    onClick={() => {
                      setSelectedQuadrantData({
                        title: 'Operational',
                        subtitle: 'High Probability × Low Impact',
                        risks: dashboard.inherent_heatmap.high_low || [],
                        color: 'yellow'
                      });
                      setShowQuadrantModal(true);
                    }}
                    className="bg-yellow-400 rounded-lg p-4 min-h-[150px] border-2 border-yellow-500 cursor-pointer hover:shadow-lg transition-all"
                  >
                    <div className="text-white mb-2">
                      <h4 className="font-bold text-sm">Operational</h4>
                      <p className="text-xs opacity-90">High Prob × Low Impact</p>
                    </div>
                    <div className="bg-white/20 rounded p-2 text-white">
                      <p className="font-bold text-lg">{dashboard.inherent_heatmap.high_low?.length || 0}</p>
                      <p className="text-xs">risks</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Residual Risk Heatmap */}
              <div className="bg-white rounded-xl border-2 border-green-300 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  Residual Risk Matrix (After Mitigation)
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {/* Top Left */}
                  <div 
                    onClick={() => {
                      setSelectedQuadrantData({
                        title: 'Severe but Unlikely (Residual)',
                        subtitle: 'Low Probability × High Impact (After Mitigation)',
                        risks: dashboard.residual_heatmap.low_high || [],
                        color: 'orange'
                      });
                      setShowQuadrantModal(true);
                    }}
                    className="bg-orange-400 rounded-lg p-4 min-h-[150px] border-2 border-orange-500 cursor-pointer hover:shadow-lg transition-all"
                  >
                    <div className="text-white mb-2">
                      <h4 className="font-bold text-sm">Severe but Unlikely</h4>
                      <p className="text-xs opacity-90">Low Prob × High Impact</p>
                    </div>
                    <div className="bg-white/20 rounded p-2 text-white">
                      <p className="font-bold text-lg">{dashboard.residual_heatmap.low_high?.length || 0}</p>
                      <p className="text-xs">risks</p>
                    </div>
                  </div>

                  {/* Top Right */}
                  <div 
                    onClick={() => {
                      setSelectedQuadrantData({
                        title: '⚠️ BOARD-CRITICAL (Residual)',
                        subtitle: 'High Probability × High Impact (After Mitigation)',
                        risks: dashboard.residual_heatmap.high_high || [],
                        color: 'red'
                      });
                      setShowQuadrantModal(true);
                    }}
                    className="bg-red-500 rounded-lg p-4 min-h-[150px] border-2 border-red-600 cursor-pointer hover:shadow-lg transition-all"
                  >
                    <div className="text-white mb-2">
                      <h4 className="font-bold text-sm">⚠️ BOARD-CRITICAL</h4>
                      <p className="text-xs opacity-90">High Prob × High Impact</p>
                    </div>
                    <div className="bg-white/20 rounded p-2 text-white">
                      <p className="font-bold text-lg">{dashboard.residual_heatmap.high_high?.length || 0}</p>
                      <p className="text-xs">risks</p>
                    </div>
                  </div>

                  {/* Bottom Left */}
                  <div 
                    onClick={() => {
                      setSelectedQuadrantData({
                        title: 'Acceptable (Residual)',
                        subtitle: 'Low Probability × Low Impact (After Mitigation)',
                        risks: dashboard.residual_heatmap.low_low || [],
                        color: 'green'
                      });
                      setShowQuadrantModal(true);
                    }}
                    className="bg-green-400 rounded-lg p-4 min-h-[150px] border-2 border-green-500 cursor-pointer hover:shadow-lg transition-all"
                  >
                    <div className="text-white mb-2">
                      <h4 className="font-bold text-sm">Acceptable</h4>
                      <p className="text-xs opacity-90">Low Prob × Low Impact</p>
                    </div>
                    <div className="bg-white/20 rounded p-2 text-white">
                      <p className="font-bold text-lg">{dashboard.residual_heatmap.low_low?.length || 0}</p>
                      <p className="text-xs">risks</p>
                    </div>
                  </div>

                  {/* Bottom Right */}
                  <div 
                    onClick={() => {
                      setSelectedQuadrantData({
                        title: 'Operational (Residual)',
                        subtitle: 'High Probability × Low Impact (After Mitigation)',
                        risks: dashboard.residual_heatmap.high_low || [],
                        color: 'yellow'
                      });
                      setShowQuadrantModal(true);
                    }}
                    className="bg-yellow-400 rounded-lg p-4 min-h-[150px] border-2 border-yellow-500 cursor-pointer hover:shadow-lg transition-all"
                  >
                    <div className="text-white mb-2">
                      <h4 className="font-bold text-sm">Operational</h4>
                      <p className="text-xs opacity-90">High Prob × Low Impact</p>
                    </div>
                    <div className="bg-white/20 rounded p-2 text-white">
                      <p className="font-bold text-lg">{dashboard.residual_heatmap.high_low?.length || 0}</p>
                      <p className="text-xs">risks</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Risk Movement Analysis */}
            <div className="bg-white rounded-xl border-2 border-gray-300 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Risk Movement Analysis</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-red-600" />
                    <h4 className="font-bold text-red-900">New High Risks</h4>
                  </div>
                  <p className="text-3xl font-bold text-red-700">{dashboard.statistics.risks_worsened}</p>
                  <p className="text-xs text-red-600 mt-1">Entered Board-Critical</p>
                </div>

                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h4 className="font-bold text-green-900">Reduced Risks</h4>
                  </div>
                  <p className="text-3xl font-bold text-green-700">{dashboard.statistics.risks_improved}</p>
                  <p className="text-xs text-green-600 mt-1">Exited Board-Critical</p>
                </div>

                <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                    <h4 className="font-bold text-orange-900">Stagnant Critical</h4>
                  </div>
                  <p className="text-3xl font-bold text-orange-700">{dashboard.statistics.risks_stagnant}</p>
                  <p className="text-xs text-orange-600 mt-1">Need Attention</p>
                </div>
              </div>

              {/* Detailed Risk Lists */}
              <div className="space-y-4">
                {/* New High Risks */}
                {dashboard.risk_movement.new_high_risks.length > 0 && (
                  <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
                    <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Risks That Entered Board-Critical Quadrant
                    </h4>
                    <div className="space-y-2">
                      {dashboard.risk_movement.new_high_risks.map((risk, i) => (
                        <div key={i} className="bg-white border border-red-200 rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-900">{risk.title}</p>
                            <p className="text-sm text-gray-600">{risk.category} • Owner: {risk.owner}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">{risk.previous_level} →</p>
                            <p className="text-sm font-bold text-red-700">{risk.current_level}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reduced Risks */}
                {dashboard.risk_movement.reduced_risks.length > 0 && (
                  <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded">
                    <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Risks That Exited Board-Critical (Mitigation Effective)
                    </h4>
                    <div className="space-y-2">
                      {dashboard.risk_movement.reduced_risks.map((risk, i) => (
                        <div key={i} className="bg-white border border-green-200 rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-900">{risk.title}</p>
                            <p className="text-sm text-gray-600">{risk.category} • Owner: {risk.owner}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">{risk.previous_level} →</p>
                            <p className="text-sm font-bold text-green-700">{risk.current_level}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stagnant Critical Risks */}
                {dashboard.risk_movement.stagnant_critical.length > 0 && (
                  <div className="border-l-4 border-orange-500 bg-orange-50 p-4 rounded">
                    <h4 className="font-bold text-orange-900 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Stagnant Board-Critical Risks (Require Action)
                    </h4>
                    <div className="space-y-2">
                      {dashboard.risk_movement.stagnant_critical.map((risk, i) => (
                        <div key={i} className="bg-white border border-orange-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-gray-900">{risk.title}</p>
                            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                              Still Critical
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">{risk.category} • Owner: {risk.owner}</span>
                            <span className="text-orange-700">Mitigation: {risk.mitigation_status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Movement */}
                {dashboard.risk_movement.new_high_risks.length === 0 && 
                 dashboard.risk_movement.reduced_risks.length === 0 && 
                 dashboard.risk_movement.stagnant_critical.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No significant risk movements detected. All risks are stable.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Risk Assessment Studies View */}
        {activeView === 'studies' && !openStudyDetail && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Risk Assessment Studies</h2>
              <button
                onClick={() => setShowCreateStudy(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Risk Study
              </button>
            </div>

            {studies.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Risk Studies Yet</h3>
                <p className="text-gray-600 mb-4">Create your first risk assessment study to get started</p>
                <button
                  onClick={() => setShowCreateStudy(true)}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create First Study
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {studies.map((studyItem) => (
                  <div
                    key={studyItem.id}
                    onClick={() => setOpenStudyDetail(studyItem)}
                    className="bg-white rounded-xl border-2 border-gray-200 p-5 hover:border-indigo-400 hover:shadow-lg transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">{studyItem.study_name}</h3>
                        <p className="text-sm text-indigo-600 mt-1">{studyItem.study_period}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        studyItem.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {studyItem.status}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(studyItem.assessment_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        <span>{studyItem.business_type}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span className="text-xs">Created by {studyItem.created_by_email}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <button className="text-sm text-indigo-600 font-medium flex items-center gap-1 hover:text-indigo-700">
                        Open Study <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Study Detail View */}
        {activeView === 'studies' && openStudyDetail && (
          <StudyDetailView
            study={openStudyDetail}
            onBack={() => setOpenStudyDetail(null)}
            onRefresh={fetchData}
            user={user}
          />
        )}

        {activeView === 'list' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">My Active Risks</h3>
            {risks.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No risks created yet. Click "Add Risk" to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {risks.map((risk) => (
                  <div
                    key={risk.id}
                    onClick={() => { setSelectedRisk(risk); setShowRiskDetail(true); }}
                    className="border border-gray-200 rounded-lg p-4 hover:border-red-400 hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900">{risk.risk_title}</h4>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            risk.risk_level === 'Board-Critical' ? 'bg-red-100 text-red-700' :
                            risk.risk_level === 'Severe but Unlikely' ? 'bg-orange-100 text-orange-700' :
                            risk.risk_level === 'Operational' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {risk.risk_level}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{risk.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            {getCategoryIcon(risk.category)}
                            {risk.category}
                          </span>
                          {risk.risk_owner && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {risk.risk_owner}
                            </span>
                          )}
                          {risk.status && (
                            <span className={`px-2 py-0.5 rounded ${
                              risk.status === 'Open' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {risk.status}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 text-xs ml-4">
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded font-medium whitespace-nowrap">
                          Impact: {risk.impact_score}
                        </span>
                        <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded font-medium whitespace-nowrap">
                          Probability: {risk.likelihood_score}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 2x2 Heatmap View */}
        {activeView === 'heatmap' && heatmap && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-6">2×2 Risk Matrix (Probability × Criticality)</h3>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Top Left: Low Probability, High Impact */}
                <div className="bg-orange-500 rounded-xl p-6 min-h-[200px] border-4 border-orange-600">
                  <div className="text-white mb-3">
                    <h4 className="font-bold text-lg">Severe but Unlikely</h4>
                    <p className="text-sm opacity-90">Low Probability × High Impact</p>
                    <p className="text-xs mt-1 opacity-75">→ Contingency & Resilience</p>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3 text-white">
                    <p className="font-bold mb-2">{heatmap.heatmap.low_high?.length || 0} Risks</p>
                    <div className="space-y-1">
                      {(heatmap.heatmap.low_high || []).slice(0, 3).map((r, i) => (
                        <div key={i} className="text-xs bg-black/20 px-2 py-1 rounded truncate">
                          {r.title}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Top Right: High Probability, High Impact */}
                <div className="bg-red-500 rounded-xl p-6 min-h-[200px] border-4 border-red-700">
                  <div className="text-white mb-3">
                    <h4 className="font-bold text-lg">⚠️ BOARD-CRITICAL</h4>
                    <p className="text-sm opacity-90">High Probability × High Impact</p>
                    <p className="text-xs mt-1 opacity-75">→ Mandatory Board Oversight</p>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3 text-white">
                    <p className="font-bold mb-2">{heatmap.heatmap.high_high?.length || 0} Risks</p>
                    <div className="space-y-1">
                      {(heatmap.heatmap.high_high || []).slice(0, 3).map((r, i) => (
                        <div key={i} className="text-xs bg-black/20 px-2 py-1 rounded truncate">
                          {r.title}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bottom Left: Low Probability, Low Impact */}
                <div className="bg-green-400 rounded-xl p-6 min-h-[200px] border-4 border-green-500">
                  <div className="text-white mb-3">
                    <h4 className="font-bold text-lg">Acceptable</h4>
                    <p className="text-sm opacity-90">Low Probability × Low Impact</p>
                    <p className="text-xs mt-1 opacity-75">→ Monitor</p>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3 text-white">
                    <p className="font-bold mb-2">{heatmap.heatmap.low_low?.length || 0} Risks</p>
                    <div className="space-y-1">
                      {(heatmap.heatmap.low_low || []).slice(0, 3).map((r, i) => (
                        <div key={i} className="text-xs bg-black/20 px-2 py-1 rounded truncate">
                          {r.title}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bottom Right: High Probability, Low Impact */}
                <div className="bg-yellow-400 rounded-xl p-6 min-h-[200px] border-4 border-yellow-500">
                  <div className="text-white mb-3">
                    <h4 className="font-bold text-lg">Operational</h4>
                    <p className="text-sm opacity-90">High Probability × Low Impact</p>
                    <p className="text-xs mt-1 opacity-75">→ Management Level</p>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3 text-white">
                    <p className="font-bold mb-2">{heatmap.heatmap.high_low?.length || 0} Risks</p>
                    <div className="space-y-1">
                      {(heatmap.heatmap.high_low || []).slice(0, 3).map((r, i) => (
                        <div key={i} className="text-xs bg-black/20 px-2 py-1 rounded truncate">
                          {r.title}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Axis Labels */}
              <div className="flex justify-between text-sm font-semibold text-gray-700 mt-6">
                <span>← Low Probability</span>
                <span>High Probability →</span>
              </div>
              <div className="text-center text-sm font-semibold text-gray-700 mt-2">
                ↑ Low Impact | High Impact ↑
              </div>
            </div>
          </div>
        )}

        {/* Risk Library View */}
        {activeView === 'library' && riskLibrary && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Risk Library - {selectedIndustry === 'All' ? 'All Industries' : selectedIndustry}
                </h3>
                <p className="text-sm text-gray-600 mt-1">Pre-built risk templates across {riskLibrary.categories?.length || 0} categories</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">{riskLibrary.total_count}</span> risks available
                </div>
                <select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg font-medium bg-white"
                >
                  <option value="All">🌐 All Industries</option>
                  <option value="All Industries">📚 Cross-Industry Risks Only</option>
                  <option value="Manufacturing">🏭 Manufacturing/Steel</option>
                  <option value="Renewable Energy">⚡ Renewable Energy</option>
                </select>
              </div>
            </div>

            {/* Category Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {Object.entries(riskLibrary.by_category).map(([category, catRisks]) => (
                <div key={category} className="border border-gray-300 rounded-lg p-3 hover:border-red-400 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    {getCategoryIcon(category)}
                    <h5 className="font-semibold text-gray-900 text-sm">{category}</h5>
                  </div>
                  <p className="text-2xl font-bold text-red-600">{catRisks.length}</p>
                  <p className="text-xs text-gray-500">risks</p>
                </div>
              ))}
            </div>

            {/* Industry Coverage Info - Show when All is selected */}
            {selectedIndustry === 'All' && (
              <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h5 className="font-semibold text-indigo-900 mb-2">📊 Viewing Risks from All Industries</h5>
                <div className="flex flex-wrap gap-2">
                  {riskLibrary.industries?.map((ind, i) => (
                    <span key={i} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                      {ind === 'Manufacturing' ? '🏭' : '⚡'} {ind}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-indigo-700 mt-2">
                  💡 Tip: Use the dropdown above to filter risks by specific industry
                </p>
              </div>
            )}

            {/* Detailed Risk List by Category */}
            {Object.entries(riskLibrary.by_category).map(([category, catRisks]) => (
              <div key={category} className="mb-8">
                <div className="bg-gray-100 px-4 py-3 rounded-lg mb-3">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2">
                    {getCategoryIcon(category)}
                    {category} <span className="text-sm font-normal text-gray-600">({catRisks.length} risks)</span>
                  </h4>
                </div>
                <div className="space-y-3 pl-4">
                  {catRisks.map((risk, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-red-300 transition-all">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-semibold text-gray-900">{risk.risk_title}</h5>
                            {risk.kri_source && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                KRI: {risk.kri_source}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{risk.description}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              risk.industry === 'Manufacturing' 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {risk.industry === 'Manufacturing' ? '🏭' : '⚡'} {risk.industry}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 text-xs ml-4">
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded font-medium whitespace-nowrap">
                            Impact: {risk.default_impact}
                          </span>
                          <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded font-medium whitespace-nowrap">
                            Probability: {risk.default_likelihood}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Summary Footer */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <div>
                  Showing <span className="font-semibold text-gray-900">{riskLibrary.total_count}</span> risk templates
                  {selectedIndustry !== 'All' && (
                    <span> for <span className="font-semibold text-gray-900">{selectedIndustry}</span></span>
                  )}
                </div>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Custom Risk
                </button>
              </div>
            </div>
          </div>
        )}

        {/* High-Level Scoring View (All Risks) */}
        {activeView === 'scoring' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">All Risks - Scoring Overview</h3>
            {risks.length === 0 ? (
              <p className="text-center text-gray-600 py-8">No risks to display</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Risk Title</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Category</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Inherent Impact</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Inherent Probability</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Inherent Level</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Residual Impact</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Residual Probability</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Residual Level</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {risks.map((risk) => {
                      const residualImpact = risk.residual_impact || risk.impact_score;
                      const residualLikelihood = risk.residual_likelihood || risk.likelihood_score;
                      return (
                        <tr key={risk.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{risk.risk_title}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{risk.category}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              risk.impact_score === 'High' ? 'bg-red-100 text-red-700' :
                              risk.impact_score === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {risk.impact_score}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              risk.likelihood_score === 'High' ? 'bg-red-100 text-red-700' :
                              risk.likelihood_score === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {risk.likelihood_score}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              risk.risk_level === 'Board-Critical' ? 'bg-red-100 text-red-700' :
                              risk.risk_level === 'Severe but Unlikely' ? 'bg-orange-100 text-orange-700' :
                              risk.risk_level === 'Operational' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {risk.risk_level}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              residualImpact === 'High' ? 'bg-red-100 text-red-700' :
                              residualImpact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {residualImpact}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              residualLikelihood === 'High' ? 'bg-red-100 text-red-700' :
                              residualLikelihood === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {residualLikelihood}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              (residualLikelihood === 'High' && residualImpact === 'High') ? 'bg-red-100 text-red-700' :
                              (residualLikelihood === 'Low' && residualImpact === 'High') ? 'bg-orange-100 text-orange-700' :
                              (residualLikelihood === 'High' && residualImpact === 'Low') ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {(residualLikelihood === 'High' && residualImpact === 'High') ? 'Board-Critical' :
                               (residualLikelihood === 'Low' && residualImpact === 'High') ? 'Severe' :
                               (residualLikelihood === 'High' && residualImpact === 'Low') ? 'Operational' :
                               'Acceptable'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => { setSelectedRisk(risk); setShowRiskDetail(true); }}
                              className="text-red-600 hover:text-red-700 text-sm font-medium"
                            >
                              Edit Scores
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* High-Level Mitigation View (All Risks) */}
        {activeView === 'mitigation' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">All Risks - Mitigation Status</h3>
            {risks.length === 0 ? (
              <p className="text-center text-gray-600 py-8">No risks to display</p>
            ) : (
              <div className="space-y-4">
                {risks.map((risk) => {
                  const hasPreventive = risk.preventive_controls && risk.preventive_controls.length > 0;
                  const hasDetective = risk.detective_controls && risk.detective_controls.length > 0;
                  const hasCorrective = risk.corrective_actions && risk.corrective_actions.length > 0;
                  const controlCount = (hasPreventive ? risk.preventive_controls.length : 0) +
                                      (hasDetective ? risk.detective_controls.length : 0) +
                                      (hasCorrective ? risk.corrective_actions.length : 0);
                  
                  return (
                    <div key={risk.id} className="border border-gray-200 rounded-lg p-4 hover:border-red-300 transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{risk.risk_title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{risk.category}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          risk.risk_level === 'Board-Critical' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {risk.risk_level}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className={`p-3 rounded-lg ${hasPreventive ? 'bg-green-50 border-2 border-green-300' : 'bg-gray-50 border border-gray-200'}`}>
                          <p className="text-xs text-gray-600 mb-1">Preventive</p>
                          <p className="text-lg font-bold text-gray-900">{hasPreventive ? risk.preventive_controls.length : 0}</p>
                        </div>
                        <div className={`p-3 rounded-lg ${hasDetective ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-gray-50 border border-gray-200'}`}>
                          <p className="text-xs text-gray-600 mb-1">Detective</p>
                          <p className="text-lg font-bold text-gray-900">{hasDetective ? risk.detective_controls.length : 0}</p>
                        </div>
                        <div className={`p-3 rounded-lg ${hasCorrective ? 'bg-orange-50 border-2 border-orange-300' : 'bg-gray-50 border border-gray-200'}`}>
                          <p className="text-xs text-gray-600 mb-1">Corrective</p>
                          <p className="text-lg font-bold text-gray-900">{hasCorrective ? risk.corrective_actions.length : 0}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          Total Controls: <span className="font-semibold">{controlCount}</span>
                          {risk.risk_owner && <span className="ml-4">Owner: <span className="font-medium">{risk.risk_owner}</span></span>}
                        </div>
                        <button
                          onClick={() => { setSelectedRisk(risk); setShowRiskDetail(true); }}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Manage Controls
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* High-Level KRIs View (All Risks) */}
        {activeView === 'kris' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Key Risk Indicators (KRIs) - All Risks</h3>
            {signals && signals.total_signals > 0 ? (
              <div className="space-y-4">
                <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 mb-4">
                  <h4 className="font-bold text-purple-900 mb-2">Active KRI Signals: {signals.total_signals}</h4>
                  <p className="text-sm text-purple-800">Operational signals from ERS, CBAM, and Safety modules</p>
                </div>
                
                {signals.signals.map((signal, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{signal.suggested_risk}</h4>
                        <p className="text-sm text-gray-600 mt-1">{signal.risk_category}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        signal.severity === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {signal.severity}
                      </span>
                    </div>
                    <div className="bg-gray-50 rounded p-3 mt-2">
                      <p className="text-sm text-gray-700"><strong>Signal:</strong> {signal.signal_type}</p>
                      <p className="text-sm text-gray-700 mt-1"><strong>Trigger:</strong> {signal.trigger_value} (Threshold: {signal.threshold})</p>
                      <p className="text-sm text-gray-900 mt-2">{signal.recommendation}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No KRI signals detected</p>
                <p className="text-sm text-gray-500 mt-2">KRIs are monitored from operational modules (ERS, CBAM, Safety)</p>
              </div>
            )}
          </div>
        )}

        {/* High-Level History & Audit View (All Risks) */}
        {activeView === 'history' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Audit Trail - All Risk Changes</h3>
            <AuditLogAggregateView />
          </div>
        )}

        {/* Create Risk Form Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-red-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Add New Risk</h3>
                <button
                  onClick={() => setShowLibraryPicker(!showLibraryPicker)}
                  className="px-3 py-1 bg-white text-red-600 rounded text-sm hover:bg-red-50"
                >
                  {showLibraryPicker ? 'Manual Entry' : 'Select from Library'}
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Library Picker */}
                {showLibraryPicker && riskLibrary && (
                  <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <h4 className="font-semibold text-indigo-900 mb-3">Select from {selectedIndustry} Library:</h4>
                    <div className="space-y-2">
                      {riskLibrary.risks.map((libRisk, i) => (
                        <button
                          key={i}
                          onClick={() => selectFromLibrary(libRisk)}
                          className="w-full text-left px-3 py-2 bg-white border border-indigo-200 rounded hover:bg-indigo-50 hover:border-indigo-400 transition-all"
                        >
                          <div className="font-medium text-gray-900">{libRisk.risk_title}</div>
                          <div className="text-xs text-gray-600">{libRisk.category} | I:{libRisk.default_impact} P:{libRisk.default_likelihood}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Risk Title *</label>
                  <input
                    type="text"
                    value={newRisk.risk_title}
                    onChange={(e) => setNewRisk({...newRisk, risk_title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={newRisk.category}
                    onChange={(e) => setNewRisk({...newRisk, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Financial">Financial & Strategic</option>
                    <option value="Operational">Operational</option>
                    <option value="Compliance & Legal">Compliance & Legal</option>
                    <option value="ESG & Sustainability">ESG & Sustainability</option>
                    <option value="People & Safety">People & Safety</option>
                    <option value="Cyber & Information Security">Cyber & Information Security</option>
                    <option value="Regulatory & Policy">Regulatory & Policy</option>
                    <option value="Third-Party & Supply Chain">Third-Party & Supply Chain</option>
                    <option value="Culture & Ethics">Culture & Ethics</option>
                    <option value="Reputational">Reputational</option>
                    <option value="Competition & Market">Competition & Market</option>
                    <option value="Others">Others</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newRisk.description}
                    onChange={(e) => setNewRisk({...newRisk, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Impact (Criticality) *</label>
                    <select
                      value={newRisk.impact_score}
                      onChange={(e) => setNewRisk({...newRisk, impact_score: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Low">Low - Localised, minor</option>
                      <option value="Medium">Medium - Material</option>
                      <option value="High">High - Severe enterprise-wide</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Probability *</label>
                    <select
                      value={newRisk.likelihood_score}
                      onChange={(e) => setNewRisk({...newRisk, likelihood_score: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Low">Low - Rare</option>
                      <option value="Medium">Medium - Occasional</option>
                      <option value="High">High - Recurring</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => { setShowCreateForm(false); setShowLibraryPicker(false); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createRisk}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Create Risk
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Risk Detail Modal with 5 Tabs */}
        {showRiskDetail && selectedRisk && (
          <RiskDetailModal
            risk={selectedRisk}
            onClose={() => { setShowRiskDetail(false); setSelectedRisk(null); fetchData(); }}
            user={user}
          />
        )}

        {/* Create Study Modal */}
        {showCreateStudy && (
          <CreateStudyModal
            onClose={() => setShowCreateStudy(false)}
            onSuccess={() => { setShowCreateStudy(false); fetchData(); }}
            businessType={businessType}
          />
        )}

        {/* Risk Quadrant Modal */}
        {showQuadrantModal && selectedQuadrantData && (
          <RiskQuadrantModal
            quadrantData={selectedQuadrantData}
            onClose={() => { setShowQuadrantModal(false); setSelectedQuadrantData(null); }}
          />
        )}
      </div>
    </div>
  );
};

// ==================== CREATE STUDY MODAL ====================
const CreateStudyModal = ({ onClose, onSuccess, businessType }) => {
  const [formData, setFormData] = useState({
    study_name: '',
    study_period: '',
    assessment_date: new Date().toISOString().split('T')[0],
    business_type: businessType
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.study_name || !formData.study_period) {
      showWarning('Please fill in study name and period');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/erm/studies`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Risk study created successfully!');
      onSuccess();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full">
        <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Create New Risk Study</h3>
          <button onClick={onClose} className="text-white hover:bg-indigo-700 rounded p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-900">
              <strong>Risk Study:</strong> A time-bound assessment (e.g., quarterly, annual) where all risks for that period are grouped together.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Study Name *</label>
            <input
              type="text"
              value={formData.study_name}
              onChange={(e) => setFormData({ ...formData, study_name: e.target.value })}
              placeholder="e.g., Q1 2025 Risk Assessment"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Study Period *</label>
            <input
              type="text"
              value={formData.study_period}
              onChange={(e) => setFormData({ ...formData, study_period: e.target.value })}
              placeholder="e.g., Q1 2025, FY 2024-25, Jan-Mar 2025"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Date *</label>
            <input
              type="date"
              value={formData.assessment_date}
              onChange={(e) => setFormData({ ...formData, assessment_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
            <select
              value={formData.business_type}
              onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Manufacturing">🏭 Manufacturing/Steel</option>
              <option value="Renewable Energy">⚡ Renewable Energy</option>
              <option value="Technology">💻 Technology/IT</option>
              <option value="Healthcare">🏥 Healthcare</option>
              <option value="Financial Services">💰 Financial Services</option>
              <option value="Retail">🛒 Retail & E-commerce</option>
              <option value="Others">📦 Others</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Creating...' : 'Create Study'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== STUDY DETAIL VIEW COMPONENT ====================
const StudyDetailView = ({ study, onBack, onRefresh, user }) => {
  const [studyRisks, setStudyRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('risks');
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showRiskDetail, setShowRiskDetail] = useState(false);
  const [showAddRisk, setShowAddRisk] = useState(false);
  const [aiOverview, setAiOverview] = useState(null);
  const [generatingOverview, setGeneratingOverview] = useState(false);

  useEffect(() => {
    fetchStudyData();
  }, [study.id]);

  const fetchStudyData = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/erm/studies/${study.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudyRisks(res.data.risks || []);
    } catch (error) {
      console.error('Error fetching study data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAIOverview = async () => {
    setGeneratingOverview(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/erm/studies/${study.id}/ai-overview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiOverview(res.data);
    } catch (error) {
      showError('Error generating overview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingOverview(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
      >
        <span>←</span> Back to Studies
      </button>

      {/* Study Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2">{study.study_name}</h2>
            <p className="text-indigo-100">Period: {study.study_period}</p>
            <p className="text-indigo-100 text-sm mt-1">
              Assessment Date: {new Date(study.assessment_date).toLocaleDateString()} • 
              Business Type: {study.business_type}
            </p>
          </div>
          <span className={`px-4 py-2 rounded-lg font-medium ${
            study.status === 'Active' ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-700'
          }`}>
            {study.status}
          </span>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-xs text-indigo-100">Total Risks</p>
            <p className="text-2xl font-bold">{studyRisks.length}</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-xs text-indigo-100">Board-Critical</p>
            <p className="text-2xl font-bold">
              {studyRisks.filter(r => r.risk_level === 'Board-Critical').length}
            </p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-xs text-indigo-100">With Mitigation</p>
            <p className="text-2xl font-bold">
              {studyRisks.filter(r => r.mitigation_plan).length}
            </p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-xs text-indigo-100">Assigned Owner</p>
            <p className="text-2xl font-bold">
              {studyRisks.filter(r => r.risk_owner).length}
            </p>
          </div>
        </div>
      </div>

      {/* Sub-Navigation */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-2 shadow-sm">
        <div className="flex gap-1 justify-between items-center">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('risks')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'risks' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              My Risks ({studyRisks.length})
            </button>
            <button
              onClick={() => setActiveTab('heatmap')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'heatmap' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              Risk Heatmap
            </button>
            <button
              onClick={() => setActiveTab('scoring')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'scoring' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              Scoring
            </button>
            <button
              onClick={() => setActiveTab('mitigation')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'mitigation' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              Mitigation
            </button>
            <button
              onClick={() => setActiveTab('kris')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'kris' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              KRIs
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'audit' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              History & Audit Trail
            </button>
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'overview' 
                  ? 'bg-purple-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI Summary
              </span>
            </button>
          </div>
          <button
            onClick={() => setShowAddRisk(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Risk
          </button>
        </div>
      </div>

      {/* Tab Content */}

      {/* AI Overview Tab */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">AI Study Overview & Recommendations</h3>
            <button
              onClick={generateAIOverview}
              disabled={generatingOverview}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              {generatingOverview ? 'Generating...' : 'Generate AI Overview'}
            </button>
          </div>

          {!aiOverview ? (
            <div className="text-center py-12">
              <Sparkles className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-2">AI-Powered Study Summary</h4>
              <p className="text-gray-600 mb-6">
                Get an executive summary analyzing all risks, mitigation status, and board recommendations
              </p>
              <button
                onClick={generateAIOverview}
                disabled={generatingOverview}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                {generatingOverview ? 'Generating Overview...' : 'Generate AI Overview'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-purple-900">AI-Generated Executive Summary</h4>
                  </div>
                  <span className="text-xs text-purple-700">
                    Generated: {new Date(aiOverview.generated_at).toLocaleString()} • Model: {aiOverview.model}
                  </span>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <div className="prose prose-sm max-w-none text-gray-800">
                  <ReactMarkdown>{aiOverview.overview}</ReactMarkdown>
                </div>
              </div>

              <button
                onClick={generateAIOverview}
                disabled={generatingOverview}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                🔄 Regenerate Overview
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'risks' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Risks in this Study</h3>
          {loading ? (
            <p className="text-center py-8 text-gray-600">Loading...</p>
          ) : studyRisks.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No risks in this study yet</p>
              <button
                onClick={() => setShowAddRisk(true)}
                className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add First Risk
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {studyRisks.map((risk) => (
                <div
                  key={risk.id}
                  onClick={() => { setSelectedRisk(risk); setShowRiskDetail(true); }}
                  className="border border-gray-200 rounded-lg p-4 hover:border-red-400 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{risk.risk_title}</h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          risk.risk_level === 'Board-Critical' ? 'bg-red-100 text-red-700' :
                          risk.risk_level === 'Severe but Unlikely' ? 'bg-orange-100 text-orange-700' :
                          risk.risk_level === 'Operational' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {risk.risk_level}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{risk.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{risk.category}</span>
                        {risk.risk_owner && <span>Owner: {risk.risk_owner}</span>}
                        <span className={`px-2 py-0.5 rounded ${
                          risk.status === 'Open' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {risk.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Heatmap Tab */}
      {activeTab === 'heatmap' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6">Study Risk Heatmap</h3>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 gap-4">
              {/* Low Prob, High Impact */}
              <div className="bg-orange-500 rounded-xl p-6 min-h-[200px] border-4 border-orange-600">
                <div className="text-white mb-3">
                  <h4 className="font-bold text-lg">Severe but Unlikely</h4>
                  <p className="text-sm opacity-90">Low Probability × High Impact</p>
                </div>
                <div className="bg-white/20 rounded-lg p-3 text-white">
                  <p className="font-bold mb-2">
                    {studyRisks.filter(r => r.likelihood_score === 'Low' && r.impact_score === 'High').length} Risks
                  </p>
                  <div className="space-y-1">
                    {studyRisks.filter(r => r.likelihood_score === 'Low' && r.impact_score === 'High').slice(0,3).map((r, i) => (
                      <div key={i} className="text-xs bg-black/20 px-2 py-1 rounded truncate">
                        {r.risk_title}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* High Prob, High Impact */}
              <div className="bg-red-500 rounded-xl p-6 min-h-[200px] border-4 border-red-700">
                <div className="text-white mb-3">
                  <h4 className="font-bold text-lg">⚠️ BOARD-CRITICAL</h4>
                  <p className="text-sm opacity-90">High Probability × High Impact</p>
                </div>
                <div className="bg-white/20 rounded-lg p-3 text-white">
                  <p className="font-bold mb-2">
                    {studyRisks.filter(r => r.likelihood_score === 'High' && r.impact_score === 'High').length} Risks
                  </p>
                  <div className="space-y-1">
                    {studyRisks.filter(r => r.likelihood_score === 'High' && r.impact_score === 'High').slice(0,3).map((r, i) => (
                      <div key={i} className="text-xs bg-black/20 px-2 py-1 rounded truncate">
                        {r.risk_title}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Low Prob, Low Impact */}
              <div className="bg-green-400 rounded-xl p-6 min-h-[200px] border-4 border-green-500">
                <div className="text-white mb-3">
                  <h4 className="font-bold text-lg">Acceptable</h4>
                  <p className="text-sm opacity-90">Low Probability × Low Impact</p>
                </div>
                <div className="bg-white/20 rounded-lg p-3 text-white">
                  <p className="font-bold mb-2">
                    {studyRisks.filter(r => r.likelihood_score === 'Low' && r.impact_score === 'Low').length} Risks
                  </p>
                  <div className="space-y-1">
                    {studyRisks.filter(r => r.likelihood_score === 'Low' && r.impact_score === 'Low').slice(0,3).map((r, i) => (
                      <div key={i} className="text-xs bg-black/20 px-2 py-1 rounded truncate">
                        {r.risk_title}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* High Prob, Low Impact */}
              <div className="bg-yellow-400 rounded-xl p-6 min-h-[200px] border-4 border-yellow-500">
                <div className="text-white mb-3">
                  <h4 className="font-bold text-lg">Operational</h4>
                  <p className="text-sm opacity-90">High Probability × Low Impact</p>
                </div>
                <div className="bg-white/20 rounded-lg p-3 text-white">
                  <p className="font-bold mb-2">
                    {studyRisks.filter(r => r.likelihood_score === 'High' && r.impact_score === 'Low').length} Risks
                  </p>
                  <div className="space-y-1">
                    {studyRisks.filter(r => r.likelihood_score === 'High' && r.impact_score === 'Low').slice(0,3).map((r, i) => (
                      <div key={i} className="text-xs bg-black/20 px-2 py-1 rounded truncate">
                        {r.risk_title}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Axis Labels */}
            <div className="flex justify-between text-sm font-semibold text-gray-700 mt-6">
              <span>← Low Probability</span>
              <span>High Probability →</span>
            </div>
            <div className="text-center text-sm font-semibold text-gray-700 mt-2">
              ↑ Low Impact | High Impact ↑
            </div>
          </div>
        </div>
      )}

      {/* Scoring Tab */}
      {activeTab === 'scoring' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Study Risks - Scoring</h3>
          {studyRisks.length === 0 ? (
            <p className="text-center text-gray-600 py-8">No risks to display</p>
          ) : (
            <div className="space-y-3">
              {studyRisks.map((risk) => (
                <ScoringRow key={risk.id} risk={risk} onUpdate={fetchStudyData} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mitigation Tab */}
      {activeTab === 'mitigation' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Study Risks - Mitigation</h3>
          {studyRisks.length === 0 ? (
            <p className="text-center text-gray-600 py-8">No risks to display</p>
          ) : (
            <div className="space-y-3">
              {studyRisks.map((risk) => (
                <MitigationRow key={risk.id} risk={risk} onUpdate={fetchStudyData} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* KRIs Tab */}
      {activeTab === 'kris' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Key Risk Indicators (KRIs) - Study Level</h3>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-purple-900">
              KRI signals from operational modules (ERS, CBAM, Safety) for risks in this study.
            </p>
          </div>
          <div className="text-center py-8 text-gray-600">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p>KRI monitoring for study-specific risks</p>
            <p className="text-sm text-gray-500 mt-2">Configured KRIs will appear here</p>
          </div>
        </div>
      )}

      {/* History & Audit Trail Tab */}
      {activeTab === 'audit' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">History & Audit Trail - Study Level</h3>
          <AuditLogStudyView studyId={study.id} />
        </div>
      )}

      {/* Risk Detail Modal */}
      {showRiskDetail && selectedRisk && (
        <RiskDetailModal
          risk={selectedRisk}
          onClose={() => { setShowRiskDetail(false); setSelectedRisk(null); fetchStudyData(); }}
          user={user}
        />
      )}

      {/* Add Risk Modal */}
      {showAddRisk && (
        <AddRiskToStudyModal
          study={study}
          onClose={() => setShowAddRisk(false)}
          onSuccess={() => { setShowAddRisk(false); fetchStudyData(); }}
        />
      )}
    </div>
  );
};


// ==================== SCORING ROW COMPONENT (Inline Editor) ====================
const ScoringRow = ({ risk, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [scores, setScores] = useState({
    impact: risk.impact_score,
    likelihood: risk.likelihood_score,
    residual_impact: risk.residual_impact || risk.impact_score,
    residual_likelihood: risk.residual_likelihood || risk.likelihood_score
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/erm/risks/${risk.id}`, {
        impact_score: scores.impact,
        likelihood_score: scores.likelihood,
        residual_impact: scores.residual_impact,
        residual_likelihood: scores.residual_likelihood
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Scores updated successfully!');
      setEditing(false);
      onUpdate();
    } catch (error) {
      showError('Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-2 border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-semibold text-gray-900">{risk.risk_title}</h4>
          <p className="text-sm text-gray-600">{risk.category}</p>
        </div>
        <button
          onClick={() => setEditing(!editing)}
          className="px-3 py-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium"
        >
          {editing ? 'Cancel' : '✏️ Edit Scores'}
        </button>
      </div>

      {!editing ? (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Inherent Impact</p>
            <p className={`text-lg font-bold ${
              risk.impact_score === 'High' ? 'text-red-700' : risk.impact_score === 'Medium' ? 'text-yellow-700' : 'text-green-700'
            }`}>
              {risk.impact_score}
            </p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Inherent Probability</p>
            <p className={`text-lg font-bold ${
              risk.likelihood_score === 'High' ? 'text-red-700' : risk.likelihood_score === 'Medium' ? 'text-yellow-700' : 'text-green-700'
            }`}>
              {risk.likelihood_score}
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Residual Impact</p>
            <p className={`text-lg font-bold ${
              (risk.residual_impact || risk.impact_score) === 'High' ? 'text-red-700' : 
              (risk.residual_impact || risk.impact_score) === 'Medium' ? 'text-yellow-700' : 'text-green-700'
            }`}>
              {risk.residual_impact || risk.impact_score}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Residual Probability</p>
            <p className={`text-lg font-bold ${
              (risk.residual_likelihood || risk.likelihood_score) === 'High' ? 'text-red-700' : 
              (risk.residual_likelihood || risk.likelihood_score) === 'Medium' ? 'text-yellow-700' : 'text-green-700'
            }`}>
              {risk.residual_likelihood || risk.likelihood_score}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Inherent Impact</label>
              <select
                value={scores.impact}
                onChange={(e) => setScores({...scores, impact: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Inherent Probability</label>
              <select
                value={scores.likelihood}
                onChange={(e) => setScores({...scores, likelihood: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Residual Impact</label>
              <select
                value={scores.residual_impact}
                onChange={(e) => setScores({...scores, residual_impact: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Residual Probability</label>
              <select
                value={scores.residual_likelihood}
                onChange={(e) => setScores({...scores, residual_likelihood: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Scores'}
          </button>
        </div>
      )}
    </div>
  );
};

// ==================== RISK QUADRANT MODAL ====================
const RiskQuadrantModal = ({ quadrantData, onClose }) => {
  if (!quadrantData) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className={`bg-${quadrantData.color}-600 px-6 py-4 rounded-t-xl flex items-center justify-between`}>
          <div>
            <h3 className="text-xl font-bold text-white">{quadrantData.title}</h3>
            <p className="text-sm text-white/90 mt-1">{quadrantData.subtitle}</p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
          <h4 className="font-semibold text-gray-900 mb-4">
            {quadrantData.risks.length} Risk{quadrantData.risks.length !== 1 ? 's' : ''} in this Quadrant
          </h4>
          
          {quadrantData.risks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No risks in this quadrant</p>
            </div>
          ) : (
            <div className="space-y-3">
              {quadrantData.risks.map((risk, i) => (
                <div key={i} className="border-2 border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-all">
                  <div className="flex items-start justify-between mb-2">
                    <h5 className="font-semibold text-gray-900">{risk.title}</h5>
                    <span className={`px-2 py-1 rounded text-xs font-medium bg-${quadrantData.color}-100 text-${quadrantData.color}-700`}>
                      #{i + 1}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      {risk.category}
                    </span>
                    {risk.owner && (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {risk.owner}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== ADD RISK TO STUDY MODAL ====================
const AddRiskToStudyModal = ({ study, onClose, onSuccess }) => {
  const [newRisk, setNewRisk] = useState({
    risk_title: '',
    category: 'Operational',
    description: '',
    impact_score: 'Medium',
    likelihood_score: 'Medium'
  });
  const [saving, setSaving] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [riskLibrary, setRiskLibrary] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState('All');

  useEffect(() => {
    fetchLibrary();
  }, [selectedIndustry]);

  const fetchLibrary = async () => {
    const token = localStorage.getItem('token');
    try {
      const industryParam = selectedIndustry === 'All' ? '' : `?industry=${selectedIndustry}`;
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/erm/risk-library${industryParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRiskLibrary(res.data);
    } catch (error) {
      console.error('Error fetching library:', error);
    }
  };

  const selectFromLibrary = (libRisk) => {
    setNewRisk({
      ...newRisk,
      risk_title: libRisk.risk_title,
      category: libRisk.category,
      description: libRisk.description,
      impact_score: libRisk.default_impact,
      likelihood_score: libRisk.default_likelihood
    });
    setShowLibraryPicker(false);
  };

  const handleSubmit = async () => {
    if (!newRisk.risk_title) {
      showWarning('Please enter risk title');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/erm/risks`, {
        ...newRisk,
        study_id: study.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Risk added to study successfully!');
      onSuccess();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-red-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">Add Risk to Study</h3>
            <p className="text-sm text-red-100 mt-1">{study.study_name}</p>
          </div>
          <button
            onClick={() => setShowLibraryPicker(!showLibraryPicker)}
            className="px-3 py-1 bg-white text-red-600 rounded text-sm hover:bg-red-50"
          >
            {showLibraryPicker ? 'Manual Entry' : '📚 Select from Library'}
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Library Picker */}
          {showLibraryPicker && riskLibrary && (
            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4 max-h-96 overflow-y-auto mb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-indigo-900">Select from Risk Library:</h4>
                <select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  className="px-3 py-1 border border-indigo-300 rounded text-sm"
                >
                  <option value="All">🌐 All Industries</option>
                  <option value="Manufacturing">🏭 Manufacturing</option>
                  <option value="Renewable Energy">⚡ Renewable Energy</option>
                  <option value="All Industries">📚 Cross-Industry</option>
                </select>
              </div>
              <div className="space-y-2">
                {riskLibrary.risks.map((libRisk, i) => (
                  <button
                    key={i}
                    onClick={() => selectFromLibrary(libRisk)}
                    className="w-full text-left px-3 py-2 bg-white border border-indigo-200 rounded hover:bg-indigo-50 hover:border-indigo-400 transition-all"
                  >
                    <div className="font-medium text-gray-900">{libRisk.risk_title}</div>
                    <div className="text-xs text-gray-600 mt-1">
                      {libRisk.category} | Impact: {libRisk.default_impact} | Prob: {libRisk.default_likelihood}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Risk Title *</label>
            <input
              type="text"
              value={newRisk.risk_title}
              onChange={(e) => setNewRisk({...newRisk, risk_title: e.target.value})}
              placeholder="e.g., Supply Chain Disruption"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
            <select
              value={newRisk.category}
              onChange={(e) => setNewRisk({...newRisk, category: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Financial">Financial</option>
              <option value="Operational">Operational</option>
              <option value="Compliance & Legal">Compliance & Legal</option>
              <option value="ESG & Sustainability">ESG & Sustainability</option>
              <option value="People & Safety">People & Safety</option>
              <option value="Cyber & Information Security">Cyber & Information Security</option>
              <option value="Regulatory & Policy">Regulatory & Policy</option>
              <option value="Third-Party & Supply Chain">Third-Party & Supply Chain</option>
              <option value="Culture & Ethics">Culture & Ethics</option>
              <option value="Reputational">Reputational</option>
              <option value="Competition & Market">Competition & Market</option>
              <option value="Others">Others</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={newRisk.description}
              onChange={(e) => setNewRisk({...newRisk, description: e.target.value})}
              rows={3}
              placeholder="Describe the risk..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Impact (Criticality) *</label>
              <select
                value={newRisk.impact_score}
                onChange={(e) => setNewRisk({...newRisk, impact_score: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Probability *</label>
              <select
                value={newRisk.likelihood_score}
                onChange={(e) => setNewRisk({...newRisk, likelihood_score: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Adding...' : 'Add Risk to Study'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== AUDIT LOG STUDY VIEW ====================
const AuditLogStudyView = ({ studyId }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAuditLogs = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/erm/audit-log`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAuditLogs(res.data.logs || []);
      } catch (error) {
        console.error('Error fetching audit logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, [studyId]);

  if (loading) {
    return <p className="text-center text-gray-600 py-8">Loading audit logs...</p>;
  }

  if (auditLogs.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">No audit events recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {auditLogs.slice(0, 50).map((log, i) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                log.event.includes('Created') ? 'bg-green-100 text-green-700' :
                log.event.includes('Updated') ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {log.event}
              </span>
              <span className="ml-3 text-sm text-gray-600">{log.user}</span>
            </div>
            <span className="text-xs text-gray-500">
              {new Date(log.timestamp).toLocaleString()}
            </span>
          </div>
          
          {log.old_value && Object.keys(log.old_value).length > 0 && (
            <div className="mt-3 bg-gray-50 p-3 rounded border border-gray-200">
              <div className="space-y-2 text-sm">
                {Object.entries(log.old_value).map(([key, oldVal]) => {
                  const newVal = log.new_value?.[key];
                  return (
                    <div key={key}>
                      <span className="font-medium text-gray-900 capitalize">{key.replace(/_/g, ' ')}:</span>
                      <div className="ml-4 mt-1">
                        <span className="text-red-700">Previous: </span>
                        <span className="text-gray-700">{typeof oldVal === 'object' ? JSON.stringify(oldVal) : String(oldVal)}</span>
                      </div>
                      {newVal !== undefined && (
                        <div className="ml-4 mt-1">
                          <span className="text-green-700">Updated: </span>
                          <span className="text-gray-700">{typeof newVal === 'object' ? JSON.stringify(newVal) : String(newVal)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ==================== MITIGATION ROW COMPONENT (Inline Editor) ====================
const MitigationRow = ({ risk, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [mitigationPlan, setMitigationPlan] = useState(risk.mitigation_plan || '');
  const [preventiveControls, setPreventiveControls] = useState(risk.preventive_controls || []);
  const [detectiveControls, setDetectiveControls] = useState(risk.detective_controls || []);
  const [correctiveActions, setCorrectiveActions] = useState(risk.corrective_actions || []);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/erm/risks/${risk.id}`, {
        mitigation_plan: mitigationPlan,
        preventive_controls: preventiveControls,
        detective_controls: detectiveControls,
        corrective_actions: correctiveActions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Mitigation updated successfully!');
      setEditing(false);
      onUpdate();
    } catch (error) {
      showError('Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const hasPreventive = preventiveControls.length > 0;
  const hasDetective = detectiveControls.length > 0;
  const hasCorrective = correctiveActions.length > 0;
  const controlCount = preventiveControls.length + detectiveControls.length + correctiveActions.length;

  return (
    <div className="border-2 border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-semibold text-gray-900">{risk.risk_title}</h4>
          <p className="text-sm text-gray-600">{risk.category}</p>
        </div>
        <button
          onClick={() => setEditing(!editing)}
          className="px-3 py-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium"
        >
          {editing ? 'Cancel' : '✏️ Edit Mitigation'}
        </button>
      </div>

      {!editing ? (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className={`p-3 rounded-lg ${hasPreventive ? 'bg-green-50 border-2 border-green-300' : 'bg-gray-50 border border-gray-200'}`}>
              <p className="text-xs text-gray-600 mb-1">Preventive</p>
              <p className="text-lg font-bold text-gray-900">{preventiveControls.length}</p>
            </div>
            <div className={`p-3 rounded-lg ${hasDetective ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-gray-50 border border-gray-200'}`}>
              <p className="text-xs text-gray-600 mb-1">Detective</p>
              <p className="text-lg font-bold text-gray-900">{detectiveControls.length}</p>
            </div>
            <div className={`p-3 rounded-lg ${hasCorrective ? 'bg-orange-50 border-2 border-orange-300' : 'bg-gray-50 border border-gray-200'}`}>
              <p className="text-xs text-gray-600 mb-1">Corrective</p>
              <p className="text-lg font-bold text-gray-900">{correctiveActions.length}</p>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Total Controls: <span className="font-semibold">{controlCount}</span>
            {risk.risk_owner && <span className="ml-4">Owner: <span className="font-medium">{risk.risk_owner}</span></span>}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Overall Mitigation Plan</label>
            <textarea
              value={mitigationPlan}
              onChange={(e) => setMitigationPlan(e.target.value)}
              rows={3}
              placeholder="Describe overall mitigation strategy..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <h5 className="font-semibold text-green-900 mb-2 text-sm">Preventive Controls</h5>
            <p className="text-xs text-green-700 mb-2">Actions to prevent risk from occurring</p>
            <div className="text-sm text-gray-700">
              {preventiveControls.length > 0 ? `${preventiveControls.length} controls defined` : 'No controls yet'}
            </div>
            <button
              onClick={() => setPreventiveControls([...preventiveControls, {description: '', owner: '', due_date: '', status: 'Not Started'}])}
              className="mt-2 text-xs text-green-700 hover:text-green-800 font-medium"
            >
              + Add Preventive Control
            </button>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <h5 className="font-semibold text-yellow-900 mb-2 text-sm">Detective Controls</h5>
            <p className="text-xs text-yellow-700 mb-2">Monitoring to detect when risk is materializing</p>
            <div className="text-sm text-gray-700">
              {detectiveControls.length > 0 ? `${detectiveControls.length} controls defined` : 'No controls yet'}
            </div>
            <button
              onClick={() => setDetectiveControls([...detectiveControls, {description: '', owner: '', due_date: '', status: 'Not Started'}])}
              className="mt-2 text-xs text-yellow-700 hover:text-yellow-800 font-medium"
            >
              + Add Detective Control
            </button>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <h5 className="font-semibold text-orange-900 mb-2 text-sm">Corrective Actions</h5>
            <p className="text-xs text-orange-700 mb-2">Response if risk occurs</p>
            <div className="text-sm text-gray-700">
              {correctiveActions.length > 0 ? `${correctiveActions.length} actions defined` : 'No actions yet'}
            </div>
            <button
              onClick={() => setCorrectiveActions([...correctiveActions, {description: '', owner: '', due_date: '', status: 'Not Started'}])}
              className="mt-2 text-xs text-orange-700 hover:text-orange-800 font-medium"
            >
              + Add Corrective Action
            </button>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Mitigation'}
          </button>
        </div>
      )}
    </div>
  );
};

// ==================== RISK DETAIL MODAL COMPONENT ====================
const RiskDetailModal = ({ risk, onClose, user }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [riskData, setRiskData] = useState(risk);
  const [saving, setSaving] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'scoring', label: 'Scoring', icon: TrendingUp },
    { id: 'mitigation', label: 'Mitigation', icon: Shield },
    { id: 'kris', label: 'KRIs', icon: Activity },
    { id: 'history', label: 'History & Audit', icon: CheckCircle }
  ];

  const saveRiskUpdate = async (updates) => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/erm/risks/${risk.id}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRiskData({ ...riskData, ...updates });
      showSuccess('Risk updated successfully!');
    } catch (error) {
      showError('Error updating risk: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-600 px-6 py-4 flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white">{riskData.risk_title}</h3>
            <p className="text-red-100 text-sm mt-1">{riskData.category}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-red-700 rounded-lg p-2 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 bg-gray-50 px-6">
          <div className="flex gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-red-600 text-red-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <OverviewTab risk={riskData} saveRiskUpdate={saveRiskUpdate} saving={saving} />
          )}
          {activeTab === 'scoring' && (
            <ScoringTab risk={riskData} saveRiskUpdate={saveRiskUpdate} saving={saving} />
          )}
          {activeTab === 'mitigation' && (
            <MitigationTab risk={riskData} saveRiskUpdate={saveRiskUpdate} saving={saving} />
          )}
          {activeTab === 'kris' && (
            <KRIsTab risk={riskData} />
          )}
          {activeTab === 'history' && (
            <HistoryTab risk={riskData} />
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== TAB COMPONENTS ====================

const OverviewTab = ({ risk, saveRiskUpdate, saving }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    risk_title: risk.risk_title,
    description: risk.description,
    sub_category: risk.sub_category || '',
    risk_owner: risk.risk_owner || '',
    target_closure_date: risk.target_closure_date || '',
    status: risk.status || 'Open',
    linked_business_units: risk.linked_business_units || [],
    linked_incidents: risk.linked_incidents || []
  });

  const handleSave = () => {
    saveRiskUpdate(formData);
    setEditing(false);
  };

  const addBusinessUnit = () => {
    setFormData({
      ...formData,
      linked_business_units: [...formData.linked_business_units, '']
    });
  };

  const removeBusinessUnit = (index) => {
    setFormData({
      ...formData,
      linked_business_units: formData.linked_business_units.filter((_, i) => i !== index)
    });
  };

  const updateBusinessUnit = (index, value) => {
    const updated = [...formData.linked_business_units];
    updated[index] = value;
    setFormData({ ...formData, linked_business_units: updated });
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3">Risk Classification</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <span className="text-sm text-gray-600">Category</span>
            <p className="font-bold mt-1 text-gray-900">{risk.category}</p>
          </div>
          <div>
            <span className="text-sm text-gray-600">Sub-Category</span>
            <p className="font-medium text-gray-900 mt-1">{risk.sub_category || 'Not specified'}</p>
          </div>
          <div>
            <span className="text-sm text-gray-600">Risk Level</span>
            <p className={`font-bold mt-1 ${
              risk.risk_level === 'Board-Critical' ? 'text-red-700' :
              risk.risk_level === 'Severe but Unlikely' ? 'text-orange-700' :
              risk.risk_level === 'Operational' ? 'text-yellow-700' :
              'text-green-700'
            }`}>
              {risk.risk_level}
            </p>
          </div>
        </div>
      </div>

      {!editing ? (
        <>
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Details</h4>
              <button
                onClick={() => setEditing(true)}
                className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center gap-1"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Description</span>
                <p className="text-gray-900 mt-1">{risk.description || 'No description provided'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Risk Owner</span>
                  <p className="text-gray-900 mt-1">{risk.risk_owner || 'Not assigned'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Target Closure Date</span>
                  <p className="text-gray-900 mt-1">{risk.target_closure_date || 'Not set'}</p>
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Status</span>
                <p className="text-gray-900 mt-1">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    risk.status === 'Open' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {risk.status}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Linked Business Units */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Linked Business Units / Assets</h4>
            {risk.linked_business_units && risk.linked_business_units.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {risk.linked_business_units.map((unit, i) => (
                  <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    {unit}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-blue-700">No linked business units</p>
            )}
          </div>

          {/* Linked Incidents */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-2">Linked Incidents / Audits / ESG Issues</h4>
            {risk.linked_incidents && risk.linked_incidents.length > 0 ? (
              <div className="space-y-2">
                {risk.linked_incidents.map((incident, i) => (
                  <div key={i} className="bg-white border border-purple-200 rounded px-3 py-2 text-sm text-purple-900">
                    {incident}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-purple-700">No linked incidents or issues</p>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Risk Title</label>
            <input
              type="text"
              value={formData.risk_title}
              onChange={(e) => setFormData({ ...formData, risk_title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Category</label>
            <input
              type="text"
              value={formData.sub_category}
              onChange={(e) => setFormData({ ...formData, sub_category: e.target.value })}
              placeholder="e.g., Equipment Reliability, Supplier Quality, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Risk Owner</label>
              <input
                type="text"
                value={formData.risk_owner}
                onChange={(e) => setFormData({ ...formData, risk_owner: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Closure Date</label>
              <input
                type="date"
                value={formData.target_closure_date}
                onChange={(e) => setFormData({ ...formData, target_closure_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Under Review">Under Review</option>
              <option value="Closed">Closed</option>
            </select>
          </div>

          {/* Linked Business Units - Editable */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Linked Business Units / Assets</label>
            <div className="space-y-2">
              {formData.linked_business_units.map((unit, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={unit}
                    onChange={(e) => updateBusinessUnit(index, e.target.value)}
                    placeholder="Business unit or asset name"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    onClick={() => removeBusinessUnit(index)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                onClick={addBusinessUnit}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + Add Business Unit
              </button>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Metadata</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-blue-700">Created By:</span>
            <p className="text-blue-900 font-medium">{risk.created_by_email || 'Unknown'}</p>
          </div>
          <div>
            <span className="text-blue-700">Created At:</span>
            <p className="text-blue-900 font-medium">
              {risk.created_at ? new Date(risk.created_at).toLocaleDateString() : 'Unknown'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ScoringTab = ({ risk, saveRiskUpdate, saving }) => {
  const [inherentScores, setInherentScores] = useState({
    impact: risk.impact_score,
    likelihood: risk.likelihood_score
  });
  const [residualScores, setResidualScores] = useState({
    impact: risk.residual_impact || risk.impact_score,
    likelihood: risk.residual_likelihood || risk.likelihood_score
  });

  const handleSaveScoring = () => {
    saveRiskUpdate({
      impact_score: inherentScores.impact,
      likelihood_score: inherentScores.likelihood,
      residual_impact: residualScores.impact,
      residual_likelihood: residualScores.likelihood
    });
  };

  const calculateRiskLevel = (impact, likelihood) => {
    if (likelihood === 'High' && impact === 'High') return { level: 'Board-Critical', color: 'red' };
    if (likelihood === 'Low' && impact === 'High') return { level: 'Severe but Unlikely', color: 'orange' };
    if (likelihood === 'High' && impact === 'Low') return { level: 'Operational', color: 'yellow' };
    return { level: 'Acceptable', color: 'green' };
  };

  const inherentLevel = calculateRiskLevel(inherentScores.impact, inherentScores.likelihood);
  const residualLevel = calculateRiskLevel(residualScores.impact, residualScores.likelihood);

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Scoring Guide</h4>
        <p className="text-sm text-blue-800">
          <strong>Inherent Risk:</strong> Risk level without any controls or mitigations in place.<br/>
          <strong>Residual Risk:</strong> Risk level after implementing mitigation controls.
        </p>
      </div>

      {/* Inherent Risk Scoring */}
      <div className="border-2 border-gray-300 rounded-lg p-5">
        <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          Inherent Risk (Before Mitigation)
        </h4>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Impact (Criticality)</label>
            <select
              value={inherentScores.impact}
              onChange={(e) => setInherentScores({ ...inherentScores, impact: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Low">Low - Localised, minor</option>
              <option value="Medium">Medium - Material</option>
              <option value="High">High - Severe enterprise-wide</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Probability</label>
            <select
              value={inherentScores.likelihood}
              onChange={(e) => setInherentScores({ ...inherentScores, likelihood: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Low">Low - Rare</option>
              <option value="Medium">Medium - Occasional</option>
              <option value="High">High - Recurring</option>
            </select>
          </div>
        </div>
        <div className={`bg-${inherentLevel.color}-100 border border-${inherentLevel.color}-300 rounded-lg p-3`}>
          <p className="text-sm text-gray-700">
            Risk Level: <span className={`font-bold text-${inherentLevel.color}-700`}>{inherentLevel.level}</span>
          </p>
        </div>
      </div>

      {/* Residual Risk Scoring */}
      <div className="border-2 border-green-300 rounded-lg p-5">
        <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-600" />
          Residual Risk (After Mitigation)
        </h4>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Impact (Criticality)</label>
            <select
              value={residualScores.impact}
              onChange={(e) => setResidualScores({ ...residualScores, impact: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Low">Low - Localised, minor</option>
              <option value="Medium">Medium - Material</option>
              <option value="High">High - Severe enterprise-wide</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Probability</label>
            <select
              value={residualScores.likelihood}
              onChange={(e) => setResidualScores({ ...residualScores, likelihood: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Low">Low - Rare</option>
              <option value="Medium">Medium - Occasional</option>
              <option value="High">High - Recurring</option>
            </select>
          </div>
        </div>
        <div className={`bg-${residualLevel.color}-100 border border-${residualLevel.color}-300 rounded-lg p-3`}>
          <p className="text-sm text-gray-700">
            Risk Level: <span className={`font-bold text-${residualLevel.color}-700`}>{residualLevel.level}</span>
          </p>
        </div>
      </div>

      {/* Visual Mini Heatmap */}
      <div className="bg-white border-2 border-gray-300 rounded-lg p-5">
        <h4 className="font-bold text-gray-900 mb-4">Risk Movement Visualization</h4>
        <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
          {/* Mini 2x2 Grid */}
          <div className="aspect-square bg-orange-300 rounded-lg p-3 border-2 border-orange-400 flex items-center justify-center">
            <div className="text-center">
              <p className="text-xs font-medium text-white opacity-90 mb-1">Severe but Unlikely</p>
              {inherentScores.likelihood === 'Low' && inherentScores.impact === 'High' && (
                <div className="w-8 h-8 bg-red-600 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xs border-2 border-white">I</div>
              )}
              {residualScores.likelihood === 'Low' && residualScores.impact === 'High' && (
                <div className="w-8 h-8 bg-green-600 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xs border-2 border-white mt-1">R</div>
              )}
            </div>
          </div>

          <div className="aspect-square bg-red-400 rounded-lg p-3 border-2 border-red-500 flex items-center justify-center">
            <div className="text-center">
              <p className="text-xs font-medium text-white opacity-90 mb-1">BOARD-CRITICAL</p>
              {inherentScores.likelihood === 'High' && inherentScores.impact === 'High' && (
                <div className="w-8 h-8 bg-red-600 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xs border-2 border-white">I</div>
              )}
              {residualScores.likelihood === 'High' && residualScores.impact === 'High' && (
                <div className="w-8 h-8 bg-green-600 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xs border-2 border-white mt-1">R</div>
              )}
            </div>
          </div>

          <div className="aspect-square bg-green-300 rounded-lg p-3 border-2 border-green-400 flex items-center justify-center">
            <div className="text-center">
              <p className="text-xs font-medium text-white opacity-90 mb-1">Acceptable</p>
              {inherentScores.likelihood === 'Low' && inherentScores.impact === 'Low' && (
                <div className="w-8 h-8 bg-red-600 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xs border-2 border-white">I</div>
              )}
              {residualScores.likelihood === 'Low' && residualScores.impact === 'Low' && (
                <div className="w-8 h-8 bg-green-600 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xs border-2 border-white mt-1">R</div>
              )}
            </div>
          </div>

          <div className="aspect-square bg-yellow-300 rounded-lg p-3 border-2 border-yellow-400 flex items-center justify-center">
            <div className="text-center">
              <p className="text-xs font-medium text-white opacity-90 mb-1">Operational</p>
              {inherentScores.likelihood === 'High' && inherentScores.impact === 'Low' && (
                <div className="w-8 h-8 bg-red-600 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xs border-2 border-white">I</div>
              )}
              {residualScores.likelihood === 'High' && residualScores.impact === 'Low' && (
                <div className="w-8 h-8 bg-green-600 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xs border-2 border-white mt-1">R</div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-red-600 rounded-full border-2 border-white"></div>
            <span className="text-sm text-gray-700">I = Inherent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-600 rounded-full border-2 border-white"></div>
            <span className="text-sm text-gray-700">R = Residual</span>
          </div>
        </div>
        
        {inherentLevel.level !== residualLevel.level && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-sm font-medium text-green-900">
              ✅ Mitigation Effective: {inherentLevel.level} → {residualLevel.level}
            </p>
          </div>
        )}
      </div>

      <button
        onClick={handleSaveScoring}
        disabled={saving}
        className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
      >
        {saving ? 'Saving...' : 'Save Risk Scores'}
      </button>
    </div>
  );
};

const MitigationTab = ({ risk, saveRiskUpdate, saving }) => {
  const [preventiveControls, setPreventiveControls] = useState(risk.preventive_controls || []);
  const [detectiveControls, setDetectiveControls] = useState(risk.detective_controls || []);
  const [correctiveActions, setCorrectiveActions] = useState(risk.corrective_actions || []);

  const addControl = (type) => {
    const newControl = { description: '', owner: '', due_date: '', status: 'Not Started' };
    if (type === 'preventive') {
      setPreventiveControls([...preventiveControls, newControl]);
    } else if (type === 'detective') {
      setDetectiveControls([...detectiveControls, newControl]);
    } else {
      setCorrectiveActions([...correctiveActions, newControl]);
    }
  };

  const updateControl = (type, index, field, value) => {
    if (type === 'preventive') {
      const updated = [...preventiveControls];
      updated[index][field] = value;
      setPreventiveControls(updated);
    } else if (type === 'detective') {
      const updated = [...detectiveControls];
      updated[index][field] = value;
      setDetectiveControls(updated);
    } else {
      const updated = [...correctiveActions];
      updated[index][field] = value;
      setCorrectiveActions(updated);
    }
  };

  const removeControl = (type, index) => {
    if (type === 'preventive') {
      setPreventiveControls(preventiveControls.filter((_, i) => i !== index));
    } else if (type === 'detective') {
      setDetectiveControls(detectiveControls.filter((_, i) => i !== index));
    } else {
      setCorrectiveActions(correctiveActions.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    saveRiskUpdate({
      preventive_controls: preventiveControls,
      detective_controls: detectiveControls,
      corrective_actions: correctiveActions
    });
  };

  const ControlSection = ({ title, controls, type, color }) => (
    <div className={`border-2 border-${color}-300 rounded-lg p-4 bg-${color}-50`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className={`font-bold text-${color}-900`}>{title}</h4>
        <button
          onClick={() => addControl(type)}
          className={`text-sm px-3 py-1 bg-${color}-600 text-white rounded hover:bg-${color}-700`}
        >
          + Add {title}
        </button>
      </div>
      
      {controls.length === 0 ? (
        <p className={`text-sm text-${color}-700`}>No {title.toLowerCase()} defined yet.</p>
      ) : (
        <div className="space-y-3">
          {controls.map((control, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="space-y-2">
                <input
                  type="text"
                  value={control.description}
                  onChange={(e) => updateControl(type, index, 'description', e.target.value)}
                  placeholder="Control description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={control.owner}
                    onChange={(e) => updateControl(type, index, 'owner', e.target.value)}
                    placeholder="Owner"
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <input
                    type="date"
                    value={control.due_date}
                    onChange={(e) => updateControl(type, index, 'due_date', e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <select
                    value={control.status}
                    onChange={(e) => updateControl(type, index, 'status', e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Overdue">Overdue</option>
                  </select>
                </div>
                <button
                  onClick={() => removeControl(type, index)}
                  className="text-xs text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Three Lines of Defense</h4>
        <div className="text-sm text-blue-800 space-y-1">
          <p><strong>Preventive:</strong> Actions to stop the risk from occurring</p>
          <p><strong>Detective:</strong> Monitoring to identify when risk is materializing</p>
          <p><strong>Corrective:</strong> Response actions if risk occurs</p>
        </div>
      </div>

      <ControlSection 
        title="Preventive Controls" 
        controls={preventiveControls} 
        type="preventive" 
        color="green" 
      />

      <ControlSection 
        title="Detective Controls" 
        controls={detectiveControls} 
        type="detective" 
        color="yellow" 
      />

      <ControlSection 
        title="Corrective Actions" 
        controls={correctiveActions} 
        type="corrective" 
        color="orange" 
      />

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
      >
        {saving ? 'Saving...' : 'Save All Controls & Actions'}
      </button>
    </div>
  );
};

const KRIsTab = ({ risk }) => {
  return (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="font-semibold text-purple-900 mb-2">Key Risk Indicators (KRIs)</h4>
        <p className="text-sm text-purple-800">
          KRIs from operational modules (ERS, CBAM, Safety) that can trigger this risk.
        </p>
      </div>

      {risk.kri_source ? (
        <div className="border border-gray-300 rounded-lg p-4 bg-yellow-50">
          <p className="font-medium text-gray-900">Linked KRI Source: {risk.kri_source}</p>
          <p className="text-sm text-gray-600 mt-2">
            This risk is automatically monitored from {risk.kri_source} module data.
          </p>
        </div>
      ) : (
        <div className="text-center py-8">
          <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No KRI signals configured for this risk yet.</p>
          <p className="text-sm text-gray-500 mt-2">
            KRI monitoring will be available once operational data integration is complete.
          </p>
        </div>
      )}
    </div>
  );
};

const HistoryTab = ({ risk }) => {
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAuditLog = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/erm/audit-log?risk_id=${risk.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAuditLog(res.data.logs || []);
      } catch (error) {
        console.error('Error fetching audit log:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLog();
  }, [risk.id]);

  return (
    <div className="space-y-6">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <h4 className="font-semibold text-indigo-900 mb-2">Audit Trail</h4>
        <p className="text-sm text-indigo-800">
          Complete history of all changes to this risk for governance compliance (SEBI/SGX ready).
        </p>
      </div>

      {loading ? (
        <p className="text-center text-gray-600">Loading audit log...</p>
      ) : auditLog.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No audit events recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {auditLog.map((log, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="flex items-start justify-between mb-2">
                <span className="font-medium text-gray-900">{log.event}</span>
                <span className="text-xs text-gray-500">
                  {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-gray-600">By: {log.user}</p>
              {(log.old_value || log.new_value) && (
                <div className="mt-2 text-xs bg-gray-50 p-2 rounded border border-gray-200">
                  {log.old_value && (
                    <div className="mb-1">
                      <span className="font-medium text-red-700">Old: </span>
                      <span className="text-gray-700">{JSON.stringify(log.old_value)}</span>
                    </div>
                  )}
                  {log.new_value && (
                    <div>
                      <span className="font-medium text-green-700">New: </span>
                      <span className="text-gray-700">{JSON.stringify(log.new_value)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== AUDIT LOG AGGREGATE VIEW ====================
const AuditLogAggregateView = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllAuditLogs = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/erm/audit-log`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAuditLogs(res.data.logs || []);
      } catch (error) {
        console.error('Error fetching audit logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllAuditLogs();
  }, []);

  if (loading) {
    return <p className="text-center text-gray-600 py-8">Loading audit logs...</p>;
  }

  if (auditLogs.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">No audit events recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {auditLogs.map((log, i) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                log.event.includes('Created') ? 'bg-green-100 text-green-700' :
                log.event.includes('Updated') ? 'bg-blue-100 text-blue-700' :
                log.event.includes('Deleted') ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {log.event}
              </span>
              <span className="ml-3 text-sm text-gray-600">{log.user}</span>
            </div>
            <span className="text-xs text-gray-500">
              {new Date(log.timestamp).toLocaleString()}
            </span>
          </div>
          
          {(log.old_value || log.new_value) && (
            <div className="mt-3 text-xs bg-gray-50 p-3 rounded border border-gray-200">
              {log.old_value && Object.keys(log.old_value).length > 0 && (
                <div className="mb-2">
                  <span className="font-medium text-red-700">Previous: </span>
                  <span className="text-gray-700">{JSON.stringify(log.old_value)}</span>
                </div>
              )}
              {log.new_value && Object.keys(log.new_value).length > 0 && (
                <div>
                  <span className="font-medium text-green-700">Updated: </span>
                  <span className="text-gray-700">{JSON.stringify(log.new_value)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default RiskManagement;
